import { db } from '../firebase';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { StudyPlan, OnboardingData, MissionState } from '../types/onboarding';
import * as localStore from './studyPlanLocal';

export interface StudyState {
    version: number;
    updatedAt: any;
    onboardingData: OnboardingData;
    studyPlan: StudyPlan;
    missionState?: MissionState; // Use proper MissionState type from onboarding types
    uiState?: {
        dismissedMissionKeys: Record<string, boolean>;
    };
}

const COLLECTION = 'users'; // /users/{uid}/study/onboarding

/**
 * Gets the document reference for a user's study plan.
 */
const getDocRef = (uid: string) => {
    return doc(db, COLLECTION, uid, 'study', 'onboarding');
};

/**
 * Loads the study state for a user.
 * 
 * Strategy:
 * 1. Try Firestore.
 * 2. If empty AND localStorage has data -> Migrate Local -> Firestore -> Return.
 * 3. Return Firestore data.
 */
export const getStudyState = async (uid: string, forceFresh: boolean = false): Promise<StudyState | null> => {
    if (!uid) {
        console.log("[StudyPlanStore] No UID provided");
        return null;
    }

    try {
        console.log("[StudyPlanStore] Fetching study state for user:", uid);
        const ref = getDocRef(uid);

        // If forceFresh is true, we could add cache-busting here if needed
        // For now, Firestore's default behavior should be real-time
        const snapshot = await getDoc(ref);

        if (snapshot.exists()) {
            const data = snapshot.data() as StudyState;
            console.log("[StudyPlanStore] Found study state, has plan:", !!data.studyPlan);
            return data;
        }

        console.log("[StudyPlanStore] No study state found in Firestore");

        // Firestore empty? Check migration.
        if (localStore.hasLocalState()) {
            console.log("Found legacy local state, migrating to Firestore...");
            const localData = localStore.readLocalState();

            if (localData && localData.studyPlan) {
                const newState: StudyState = {
                    version: 1,
                    updatedAt: serverTimestamp(),
                    onboardingData: localData.onboardingData,
                    studyPlan: localData.studyPlan,
                    // We could try to migrate missionState from localStorage too if we wanted,
                    // but for MVP let's start fresh with missions or keep them local until generated.
                    // Actually, let's keep it simple.
                    uiState: {
                        dismissedMissionKeys: JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}')
                    }
                };

                // Save to Firestore
                await setDoc(ref, newState);

                // Clear local? Maybe keep as backup for phase 1.
                // localStore.clearLocalState(); 

                return newState;
            }
        }

        return null;
    } catch (error) {
        console.error("Error loading study state:", error);
        // Fallback to local if offline or error?
        // For now, return null to prompt error or retry.
        return null;
    }
};

/**
 * Helper function to remove undefined values from an object
 * Firebase doesn't accept undefined values
 */
function cleanUndefinedValues(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(cleanUndefinedValues);
    }

    const cleaned: any = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            if (value !== undefined) {
                cleaned[key] = cleanUndefinedValues(value);
            }
        }
    }
    return cleaned;
}

/**
 * Saves (overwrites or merges) the study state to Firestore.
 */
export const saveStudyState = async (uid: string, state: Partial<StudyState>): Promise<void> => {
    if (!uid) {
        console.error("[StudyPlanStore] Cannot save: no UID provided");
        throw new Error("No user ID provided");
    }

    console.log("[StudyPlanStore] Saving study state for user:", uid);

    // Clean undefined values before saving
    const cleanedState = cleanUndefinedValues(state);
    console.log("[StudyPlanStore] Cleaned state:", Object.keys(cleanedState));

    const ref = getDocRef(uid);

    try {
        // We use setDoc with merge to ensure we don't wipe existing fields if we pass partial
        await setDoc(ref, {
            ...cleanedState,
            updatedAt: serverTimestamp()
        }, { merge: true });
        console.log("[StudyPlanStore] Successfully saved study state");
    } catch (error) {
        console.error("[StudyPlanStore] Error saving study state:", error);
        throw error;
    }
};

/**
 * Subscribes to changes in the study state.
 */
export const subscribeStudyState = (uid: string, callback: (state: StudyState | null) => void) => {
    if (!uid) return () => { };
    const ref = getDocRef(uid);

    return onSnapshot(ref, (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.data() as StudyState);
        } else {
            callback(null);
        }
    });
};
